import _ from 'lodash';
import { debounceTime, Subject } from 'rxjs';
import Component from '../classes/component';
import { disableIf } from '../utils/dom-utils';

const SELECTORS = {
  decrementor: '.decrementor',
  quantity: '.quantity',
  incrementor: '.incrementor',
};

class QuantitySelector extends Component<typeof SELECTORS> {
  protected _selectors = SELECTORS;

  private _changeSubject = new Subject<number>();

  private _minQuantity = 1;

  private _maxQuantity = 100;

  public addChangeListener(handler: (value: number) => void, debounceTimeMs = 0) {
    const subscription = debounceTimeMs
      ? this._changeSubject.pipe(debounceTime(debounceTimeMs)).subscribe(handler)
      : this._changeSubject.subscribe(handler);
    this._addSubscription(subscription);
  }

  private _getValue() {
    const value = Number(this._getChild('quantity').innerText);

    if (!Number.isInteger(value)) return this._minQuantity;

    return Math.max(this._minQuantity, Math.min(value, this._maxQuantity));
  }

  private _updateButtonsState() {
    disableIf(this._getChild('decrementor'), this.value <= this._minQuantity);
    disableIf(this._getChild('incrementor'), this.value >= this._maxQuantity);
  }

  public enable() {
    this._isEnabled = true;
    this.container.classList.remove('disabled');
    this._getChild('quantity').contentEditable = 'true';
  }

  public disable() {
    this._isEnabled = false;
    this.container.classList.add('disabled');
    this._getChild('quantity').contentEditable = 'false';
  }

  public get value() {
    return this._getValue();
  }

  public set value(newValue: number) {
    this._getChild('quantity').innerHTML = Math.min(newValue, this._maxQuantity).toString();
    this._changeSubject.next(this.value);
    this._updateButtonsState();
  }

  public set minQuantity(minQuantity: number | string) {
    if (_.isString(minQuantity)) return;

    if (this.value <= minQuantity) this.value = minQuantity;

    this._minQuantity = minQuantity;
  }

  public set maxQuantity(maxQuantity: number | string) {
    if (_.isString(maxQuantity)) return;

    if (this.value > maxQuantity) this.value = maxQuantity;

    this._maxQuantity = maxQuantity;
  }

  public decrement() {
    this.value = Math.max(this._minQuantity, this.value - 1);
  }

  public increment() {
    this.value = Math.min(this.value + 1, this._maxQuantity);
  }

  private _handleBlur() {
    this.value = this._getValue();
  }

  private _handleKeypress(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      (event.target as HTMLElement).blur();
    }
  }

  private _initialize() {
    const minQty = Number(this.container.dataset.min);
    if (!Number.isNaN(minQty) && minQty > 0) this._minQuantity = minQty;

    const maxQty = Number(this.container.dataset.max);
    if (!Number.isNaN(maxQty) && maxQty > -1) this._maxQuantity = maxQty;

    this._updateButtonsState();
  }

  protected _onEnter() {
    this._initialize();

    this._addListener('decrementor', 'click', this.decrement);
    this._addListener('incrementor', 'click', this.increment);
    this._addListener('quantity', 'blur', this._handleBlur);
    this._addListener('quantity', 'keypress', this._handleKeypress, null, false);
  }
}

export default QuantitySelector;
